// Copyright (c) 2024-2026 AlphaGrade. All Rights Reserved.
// Proprietary and confidential. See LICENSE file in project root.
//
// Success-page safety net. Stripe redirects back after checkout to
//   /portfolio?upgraded=true&session_id=cs_...
// We verify that session with the backend and provision the tier if the
// webhook hasn't already — belt-and-suspenders so a webhook failure can
// never leave a paying customer stuck on Free. Renders a brief banner,
// then strips the query params so a refresh won't re-trigger.

import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTier } from '../context/TierContext';

const TOKEN_KEY = 'alphagrade_auth_token';

export default function UpgradeConfirmer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshTier } = useTier();
  const [banner, setBanner] = useState(null); // 'success' | 'pending' | null
  const handled = useRef(false);
  const bannerTimer = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('upgraded') !== 'true') return;
    if (handled.current) return;
    handled.current = true;

    const sessionId = params.get('session_id');
    const token = localStorage.getItem(TOKEN_KEY);

    (async () => {
      let confirmedTier = null;
      try {
        if (sessionId && token) {
          const resp = await fetch('/api/subscription/confirm-checkout', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ session_id: sessionId }),
          });
          if (resp.ok) {
            const data = await resp.json();
            confirmedTier = data.tier;
          }
        }
      } catch {
        // Even if confirm fails, the webhook may still provision; refreshTier
        // below reflects whatever the backend ends up with.
      } finally {
        await refreshTier();
        // Show success unless we explicitly confirmed the account is still Free.
        setBanner(confirmedTier === 'free' ? 'pending' : 'success');
        // Strip the query params so a refresh doesn't re-run this.
        navigate(location.pathname, { replace: true });
        bannerTimer.current = setTimeout(() => setBanner(null), 7000);
      }
    })();
    // The dismiss timer outlives the effect otherwise, and fires setBanner on
    // an unmounted component after a route change.
    return () => {
      if (bannerTimer.current) clearTimeout(bannerTimer.current);
    };
  }, [location, navigate, refreshTier]);

  if (!banner) return null;

  const isSuccess = banner === 'success';
  return (
    <div
      role="status"
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        background: isSuccess ? '#00D4AA' : '#1e293b',
        color: isSuccess ? '#04261f' : '#e2e8f0',
        border: isSuccess ? 'none' : '1px solid #334155',
        padding: '0.7rem 1.1rem',
        borderRadius: 10,
        fontWeight: 600,
        fontSize: '0.95rem',
        boxShadow: '0 6px 24px rgba(0,0,0,0.35)',
        maxWidth: '90vw',
        textAlign: 'center',
      }}
    >
      {isSuccess
        ? '✓ Your upgrade is active — welcome aboard.'
        : 'Payment received. Your account will update momentarily — refresh if it doesn’t show right away.'}
    </div>
  );
}
